import React from 'react';
import {
  Button, Grid, GridItem, HStack,
} from '@chakra-ui/react';
import {
  FiFileText, FiFolder, FiLayers, FiTool, FiTrendingUp, FiAlertCircle, FiRefreshCw,
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { NIL } from 'uuid';
import StatCard from './StatCard';
import BreakEvenCard from './BreakEvenCard'; // item 1132
import SegmentBar from './SegmentBar';
import DashPanel from './DashPanel';
import TopCustomersCard from './TopCustomersCard';
import ActivityFeedCard, { ActivityItem } from './ActivityFeedCard';
import { DashboardMetrics } from '../../hooks/useDashboardMetrics';
import { moneyRounded as money } from '../../utils/money'; // item 327 — stealth-aware

// map an activity kind → its row icon + accent
const ACTIVITY_META: Record<string, { icon: React.ReactNode, accent: string }> = {
  proposal: { icon: <FiLayers />, accent: 'brandBlue.300' },
  invoice: { icon: <FiFileText />, accent: 'brandGreen.400' },
  workOrder: { icon: <FiTool />, accent: 'brandGreen.400' },
};

// The full dashboard bento (items 62/64/65/66/67) — the EXPANDED content behind the
// KPI strip (item 96). Pure presentation: it takes the shared metrics and renders.
const DashboardBento: React.FC<{ m: DashboardMetrics }> = ({ m }) => {
  const history = useHistory();
  return (
    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }} gap={5} alignItems="stretch">
      <GridItem colSpan={{ base: 1, md: 2 }}>
        {/* item 1141 (Chad) — pipeline = CONTRACTED money only; pending quotes moved to
            their own Opportunities card beside this one */}
        <StatCard
          label="Pipeline (contracted)"
          value={m.pipelineValue}
          format={money}
          sub="won unbilled + AR + open work + POs"
          icon={<FiTrendingUp />}
          accent="brandGreen.400"
          index={0}
          footer={(
            <SegmentBar
              segments={[
                { label: 'Approved', value: m.approved, color: 'brandGreen.400' },
                { label: 'Deposited', value: m.deposited, color: 'brandGreen.600' },
              ]}
            />
          )}
        />
      </GridItem>

      <GridItem colSpan={{ base: 1, md: 2 }}>
        <StatCard
          label="Pending opportunities"
          value={m.pendingOpportunities}
          format={money}
          sub={`${m.pending} pending quote${m.pending === 1 ? '' : 's'} + open sales orders · Total Book ${money(m.totalBook)}`}
          icon={<FiLayers />}
          accent="brandBlue.300"
          index={7}
        />
      </GridItem>

      <GridItem colSpan={{ base: 1, md: 2 }}>
        <StatCard
          label="Invoiced (FY)"
          value={m.collected}
          format={money}
          sub={`${m.invoiceCount} invoices · ${m.paidCount} paid`}
          icon={<FiFileText />}
          accent="brandBlue.300"
          index={1}
        />
      </GridItem>

      {/* stat row: outstanding (67) · recurring skeleton (64) · open WO · proposals */}
      <StatCard
        label="Outstanding"
        value={m.outstanding}
        format={money}
        sub={`${m.outstandingCount} unpaid`}
        icon={<FiAlertCircle />}
        accent="brandBlue.300"
        index={2}
      />
      {/* item 64: recurring revenue MRR + churn (lit up once item 63 landed) */}
      <StatCard
        label="Recurring (MRR)"
        value={m.mrr}
        format={money}
        // item 1132 — the yearly view of the same number rides the subtitle
        sub={`${money(m.arr)}/yr · ${m.activeRecurringCount} active · ${Math.round(m.churnRate * 100)}% churn`}
        icon={<FiRefreshCw />}
        accent="brandGreen.400"
        index={6}
      />
      <StatCard
        label="Open work orders"
        value={m.openWO}
        sub={`${m.woCount} total`}
        icon={<FiTool />}
        accent="brandGreen.400"
        index={3}
      />
      <StatCard
        label="Proposals"
        value={m.proposalCount}
        sub={`${m.pending} pending`}
        icon={<FiLayers />}
        accent="brandBlue.300"
        index={4}
      />

      {/* item 1132 — the break-even ledger: annual budget vs finalized money, month over month */}
      <GridItem colSpan={{ base: 1, md: 2 }}>
        <BreakEvenCard annualStaticBudget={m.budgetWithCommissions} monthlyOverhead={m.monthlyOverhead} fyCommissions={m.fyCommissions} />
      </GridItem>

      {/* feature row: top customers (65) · recent activity (66) */}
      <GridItem colSpan={{ base: 1, md: 2 }}>
        <TopCustomersCard customers={m.topCustomers} />
      </GridItem>
      <GridItem colSpan={{ base: 1, md: 2 }}>
        <ActivityFeedCard
          items={m.activity.map((a): ActivityItem => ({
            id: a.id,
            label: a.label,
            sub: a.sub,
            when: a.when,
            href: a.href,
            icon: ACTIVITY_META[a.kind].icon,
            accent: ACTIVITY_META[a.kind].accent,
          }))}
          onOpen={(href) => history.push(href)}
        />
      </GridItem>

      {/* footer row: projects · quick start */}
      <StatCard
        label="Projects"
        value={m.projectCount}
        sub="being tracked"
        icon={<FiFolder />}
        accent="brandGreen.400"
        index={5}
      />
      <GridItem colSpan={{ base: 1, md: 2, xl: 3 }}>
        <DashPanel label="Quick start">
          <HStack spacing={3} mt="auto" flexWrap="wrap">
            {/* item 38: invoices are generated from a work order / sales order, not created directly */}
            <Button size="sm" variant="outline" onClick={() => history.push('/WorkOrders')}>Work orders</Button>
            <Button size="sm" variant="outline" onClick={() => history.push(`/proposal/${NIL}`)}>Proposal</Button>
          </HStack>
        </DashPanel>
      </GridItem>
    </Grid>
  );
};

export default DashboardBento;
